export enum SubscriptionPlan {
  MONTHLY = 'MONTHLY',
  ANNUAL = 'ANNUAL',
}

export enum AnnualBilling {
  BILLED_MONTHLY = 'BILLED_MONTHLY',
  BILLED_YEARLY = 'BILLED_YEARLY',
}

export enum SubscriptionAccess {
  TRIAL = 'TRIAL',
  FREE = 'FREE',
  PRE_CLINICAL = 'PRE_CLINICAL',
  STEP_SHELF = 'STEP_SHELF',
}

export enum SubscriptionStatus {
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  TRIALING = 'trialing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  PAUSED = 'paused',
  FREE = 'free',
}

export interface BillingInfo {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
  tax_ids?: Array<{ value: string }>;
  default_payment_method?: {
    id: string;
  };
}

export interface Subscription {
  id?: string;
  userId?: string;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  plan?: SubscriptionPlan | null;
  annualBilling?: AnnualBilling | null;
  subscriptionAccessLevel?: SubscriptionAccess | null;
  price?: number | null;
  status?: SubscriptionStatus | null;
  trialStart?: string | null;
  trialEnd?: string | null;
  subscriptionStart?: string | null;
  subscriptionEnd?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  accessLevel: SubscriptionAccess;
  grandfathered?: boolean;
  activeDiscount?: boolean;
  discountStart?: string | null;
  discountEnd?: string | null;
  percentOff?: number | null;
  amountOff?: number | null;
  couponName?: string | null;
  discountDisplayName?: string | null;
  latestCancelReason?: string | null;
  promotionCode?: string | null;
  cancelAt?: string | null;
  renewsAt?: string | null;
  billsAt?: string | null;
  canPause?: boolean;
  pauseStartsAt?: string | null;
  pauseResumesAt?: string | null;
  groupId?: string;
  groupStatus?: 'PENDING' | 'COMPLETE' | 'EXPIRED';
  temporaryAccessEndsAt?: string | null;
  temporaryAccessSourceType?: 'INVITE' | null;
  // One-time (non-recurring) access purchase, e.g. the 5-year "Student Life"
  // option. Present when the user holds an active, non-revoked AccessPurchase.
  oneTimeAccessEndsAt?: string | null;
  oneTimeAccessProduct?: SubscriptionAccess | null;
  // Whether the user can still start the one-time free trial (backend
  // `isUserEligibleForFreeTrial`). Supplied by GET /subscription/status; absent
  // on older responses, so consumers treat missing as "not eligible".
  isEligibleForTrial?: boolean;
  institutionalAccess?: {
    accessLevel: SubscriptionAccess;
    schoolId: string;
    schoolName: string;
    contractId: string;
    startsAt: string;
    endsAt: string;
  } | null;
  institutionalAdminAccess?: {
    accessLevel: SubscriptionAccess.STEP_SHELF;
    organizationId: string;
    organizationName: string;
    role: 'ADMIN' | 'FACULTY';
    startsAt: string;
    endsAt: string;
    activeContractCount: number;
  } | null;
  expiredInstitutionalAccess?: {
    schoolId: string;
    schoolName: string;
    contractId: string;
    endedAt: string;
  } | null;
  paymentIssue?: {
    invoiceId: string;
    hostedInvoiceUrl?: string | null;
    amountDue: number;
    amountRemaining: number;
    currency: string;
    nextPaymentAttempt?: string | null;
  };
}

// Statuses under which the recurring subscription itself still grants access
// (mirrors ACTIVE_SUBSCRIPTION_STATUSES in ora-be's accessHelpers).
const LIVE_RECURRING_STATUSES = new Set<SubscriptionStatus>([
  SubscriptionStatus.ACTIVE,
  SubscriptionStatus.TRIALING,
  SubscriptionStatus.INCOMPLETE,
  SubscriptionStatus.PAST_DUE,
]);

/**
 * True when the user's elevated access comes only from a temporary
 * (complimentary) access grant — no live recurring subscription, one-time
 * access purchase, institutional access, or grandfathering behind it. These
 * users should still be able to start a checkout to (re)subscribe.
 */
export const hasComplimentaryOnlyAccess = (
  subscription: Subscription | null
): boolean =>
  Boolean(subscription?.temporaryAccessEndsAt) &&
  !LIVE_RECURRING_STATUSES.has(
    subscription?.status ?? SubscriptionStatus.FREE
  ) &&
  !subscription?.oneTimeAccessEndsAt &&
  !subscription?.institutionalAccess &&
  !subscription?.institutionalAdminAccess &&
  !subscription?.grandfathered;
